import type React from 'react';
import { useCallback, useRef, useState } from 'react';

import classNames from 'classnames';

import { REFRESH_RATE_120, useThrottle } from '../../../hooks/use_throttle';

import styles from './image_scrub.module.css';

type Props = {
  keyframePosterSrc: string | undefined;
  keyframeMapSrc: string | undefined;
  objectTitle: string;
  className?: string;
  frameWidth?: number;
};

const DEFAULT_FRAME_WIDTH = 320;

type ImageScrubState = 'initial' | 'loading' | 'loaded' | 'failed';

export function ImageScrub({
  keyframePosterSrc,
  keyframeMapSrc,
  objectTitle,
  className,
  frameWidth = DEFAULT_FRAME_WIDTH,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const keyframeMapDataRef = useRef<HTMLImageElement | ImageBitmap | null>(
    null,
  );
  const keyframeViewerRef = useRef<HTMLCanvasElement>(null);
  const lastMouseEventRef = useRef<React.MouseEvent | null>(null);

  const [imageScrubState, setImageScrubState] =
    useState<ImageScrubState>('initial');

  /**
   * Ideally we want to extract frame height from resolution object to be able
   * to support keyframes with different aspect ratios. But now in production
   * we have some assets that have incorrect height of the frame in resolution
   * object. As a workaround we decided to infer height of the frame based
   * on default aspect ratio of the pod: 16/9
   * Related backend issue:
   * https://linear.app/iconik/issue/BE-2821/isgedge-keyframe-map-has-wrong-height
   */
  const frameHeight = frameWidth * (9 / 16);

  const handleMouseMove = useThrottle(
    (event: React.MouseEvent) => {
      lastMouseEventRef.current = event;

      const rootElement = rootRef.current;
      const keyframeMapData = keyframeMapDataRef.current;
      const keyframeViewerElement = keyframeViewerRef.current;
      if (!rootElement || !keyframeMapData || !keyframeViewerElement) {
        return;
      }

      const { left, width } = rootElement.getBoundingClientRect();

      // set correct position for scrubber line
      const currentPosition = event.clientX - left;
      rootElement.style.setProperty(
        '--scrubber-position-x',
        `${currentPosition - 1}px`,
      );

      // show correct frame according to the position of cursor
      const context = keyframeViewerElement.getContext('2d');
      const keyframeMapHeight =
        keyframeMapData instanceof HTMLImageElement
          ? keyframeMapData.naturalHeight
          : keyframeMapData.height;
      const steps = keyframeMapHeight / frameHeight;
      const currentStep = Math.floor((currentPosition / width) * steps);
      const offsetY = currentStep * frameHeight;
      context?.drawImage(
        keyframeMapData,
        0,
        offsetY,
        frameWidth,
        frameHeight,
        0,
        0,
        frameWidth,
        frameHeight,
      );
    },
    REFRESH_RATE_120,
    [],
  );

  const handleKeyframeError = useCallback(() => {
    setImageScrubState('failed');
  }, []);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent) => {
      if (!keyframeMapSrc) {
        return;
      }

      // prevent stale position of the scrubber when re-entering component
      lastMouseEventRef.current = event;

      // prevent recreation of an image with hover back and forth on the slow network
      if (imageScrubState === 'loading') {
        return;
      }

      // If we have already fetched keymap for this instance move the scrubber
      // to the correct position when we scroll over thumbnail without moving mouse
      if (imageScrubState === 'loaded') {
        handleMouseMove(event);
        return;
      }

      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        image
          .decode()
          .then(() => createImageBitmap(image))
          .then((bitmap) => {
            keyframeMapDataRef.current = bitmap;
            if (lastMouseEventRef.current) {
              handleMouseMove(lastMouseEventRef.current);
            }
            setImageScrubState('loaded');
          })
          .catch((error) => {
            setImageScrubState('failed');
            throw error;
          });
      };
      image.onerror = handleKeyframeError;
      image.src = keyframeMapSrc;
      setImageScrubState('loading');
    },
    [keyframeMapSrc, imageScrubState, handleKeyframeError, handleMouseMove],
  );

  const isImageScrubFailed = imageScrubState === 'failed';
  const isImageScrubLoaded = imageScrubState === 'loaded';

  return (
    <div
      ref={rootRef}
      className={classNames(styles.image_scrub, className, {
        [styles.error]: isImageScrubFailed,
      })}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      role="img"
      aria-label={`Keyframe preview for ${objectTitle}`}
    >
      {keyframePosterSrc ? (
        <img
          alt=""
          src={keyframePosterSrc}
          className={styles.keyframe_poster}
          onError={handleKeyframeError}
        />
      ) : null}
      <canvas
        ref={keyframeViewerRef}
        className={classNames(styles.keyframe_viewer, {
          [styles.keyframe_map_loaded]: isImageScrubLoaded,
        })}
        width={frameWidth}
        height={frameHeight}
      />
      <div
        className={classNames(styles.scrubber, {
          [styles.keyframe_map_loaded]: isImageScrubLoaded,
        })}
      />
    </div>
  );
}
